import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, describeCommercialError, describeError, newIdempotencyKey } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { Badge } from '@/components/ui';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { LimitOverrideCheckbox } from '@/components/LimitOverrideCheckbox';
import { useCanDoWriteAction } from '@/stores/auth';

type ConcessionStatus = 'Active' | 'Cancelled' | 'Expired';

type Concession = {
  id: string;
  tenantId: string;
  startsAt: string;
  endsAt: string;
  reason: string;
  status: ConcessionStatus;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
};

const STATUS_TONE: Record<ConcessionStatus, 'success' | 'neutral' | 'error'> = {
  Active: 'success',
  Expired: 'neutral',
  Cancelled: 'error',
};

const STATUS_LABEL: Record<ConcessionStatus, string> = {
  Active: 'Vigente',
  Expired: 'Expirada',
  Cancelled: 'Cancelada',
};

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—';
}

export function ConcessionsPage() {
  const { tenantId } = useParams();
  // Conceder e cancelar exigem platform.concessions.write na API (2.7); diante
  // do token da API anterior, o seletor cai para o papel. A listagem fica para
  // quem chega à página. Esconder não autoriza: a rota responde 403 do mesmo jeito.
  const canWrite = useCanDoWriteAction('concessions');
  const [items, setItems] = useState<Concession[]>([]);
  const [error, setError] = useState<string | null>(null);
  // `loading` derivado da busca que ja terminou. Uma acao pede recarga
  // incrementando `reloadCount`, o que muda a chave e devolve "carregando" no
  // mesmo render — sem escrever estado dentro do efeito.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [limitOverride, setLimitOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idempotency = useIdempotencyKey();

  const requestKey = `${tenantId}:${reloadCount}`;
  const loading = loadedKey !== requestKey;
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/platform/tenants/${tenantId}/concessions`, {
          params: { page: 1, pageSize: 50 },
        });
        if (cancelled) return;
        setItems(data.items ?? []);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(describeError(err));
      } finally {
        if (!cancelled) setLoadedKey(requestKey);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, requestKey]);

  const active = items.find((c) => c.status === 'Active');

  async function grant(e: FormEvent, replaceExisting: boolean) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const url = `/platform/tenants/${tenantId}/concessions`;
      const body = {
        endsAt: new Date(endsAt).toISOString(),
        reason: reason.trim(),
        replaceExisting,
        // Só a exceção do Admin leva a marca; sem ela, o campo nem vai.
        ...(limitOverride ? { limitOverride: true } : {}),
      };
      // A mesma chave na repetição do mesmo pedido (ver useIdempotencyKey).
      await api.post(url, body, { headers: { 'Idempotency-Key': idempotency.keyFor(url, body) } });
      idempotency.settle();
      setEndsAt('');
      setReason('');
      setLimitOverride(false);
      reload();
    } catch (err) {
      idempotency.settle(err);
      setError(describeCommercialError(err, 'days'));
    } finally {
      setSubmitting(false);
    }
  }

  async function cancel(id: string) {
    const why = window.prompt('Motivo do cancelamento (obrigatório):');
    if (!why || !why.trim()) return;

    setError(null);
    try {
      await api.post(
        `/platform/tenants/${tenantId}/concessions/${id}/cancel`,
        { reason: why.trim() },
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      reload();
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <div className="page" style={{ maxWidth: 860 }}>
      <Link to={`/tenants/${tenantId}`} className="backlink">← Loja</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        Concessões de acesso
      </h2>

      {error && <div className="error-banner">{error}</div>}

      {canWrite && (
        <form className="card" onSubmit={(e) => grant(e, false)}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Conceder acesso</h3>

          {/* The single most likely misreading of this feature. Saying it here,
              next to the button, is what keeps someone from believing a grant
              also stopped the invoice. */}
          <div className="info-banner">
            A concessão libera o acesso, mas <strong>não interrompe a cobrança</strong>.
            Para deixar de cobrar, use <Link to={`/tenants/${tenantId}/discounts`}>desconto</Link>{' '}
            ou cancele a assinatura no detalhe da loja.
          </div>

          {active && (
            <div className="warning-banner">
              Já existe concessão vigente até {formatDate(active.endsAt)}. Cancele-a antes,
              ou use “Substituir vigente” para encerrá-la e criar uma nova.
            </div>
          )}

          <div className="field">
            <label htmlFor="endsAt">Vigente até</label>
            <input
              id="endsAt"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="reason">Motivo (obrigatório)</label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              minLength={5}
            />
          </div>

          <LimitOverrideCheckbox checked={limitOverride} onChange={setLimitOverride} />

          <div className="btn-row">
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Concedendo…' : 'Conceder acesso'}
            </button>
            {active && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submitting}
                onClick={(e) => grant(e, true)}
              >
                Substituir vigente
              </button>
            )}
          </div>
        </form>
      )}

      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Histórico</h3>

        {loading ? (
          <p>Carregando…</p>
        ) : items.length === 0 ? (
          <p>Nenhuma concessão registrada para esta loja.</p>
        ) : (
          <div className="table-scroll">
            <CardTable className="table">
              <THead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Vigência</Th>
                  <Th>Motivo</Th>
                  <Th>Cancelamento</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {items.map((c) => (
                  <Tr key={c.id}>
                    <Td heading><Badge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge></Td>
                    <Td label="Vigência">{formatDate(c.startsAt)} → {formatDate(c.endsAt)}</Td>
                    <Td label="Motivo">{c.reason}</Td>
                    <Td label="Cancelamento">{c.cancellationReason ?? '—'}</Td>
                    <Td action>
                      {canWrite && c.status === 'Active' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => void cancel(c.id)}>
                          Cancelar
                        </button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </CardTable>
          </div>
        )}
      </div>
    </div>
  );
}
